/**
 * Role-based routing utilities
 * Centralized logic for determining dashboard paths based on user role
 */

// Canonical roles (single source of truth)
export const ROLES = {
  LISTENER: 'listener',
  ARTIST: 'artist',
  CONTENT_CREATOR: 'content_creator',
  PODCASTER: 'podcaster',
  STREAMER: 'streamer',
  STUDIO: 'studio',
  VIDEOGRAPHER: 'videographer',
  ENGINEER: 'engineer',
  GRAPHIC_DESIGNER: 'graphic_designer',
  PRODUCER: 'producer',
  ORGANIZATION: 'organization',
  ADVERTISER: 'advertiser',
  OWNER: 'owner',
  ADMIN: 'admin'
};

// Creator roles (require subscription)
export const CREATOR_ROLES = [
  ROLES.ARTIST,
  ROLES.CONTENT_CREATOR,
  ROLES.PODCASTER,
  ROLES.STREAMER,
  ROLES.STUDIO,
  ROLES.VIDEOGRAPHER,
  ROLES.ENGINEER,
  ROLES.GRAPHIC_DESIGNER,
  ROLES.PRODUCER
];

// Role to dashboard path mapping
const DASHBOARD_PATHS = {
  [ROLES.LISTENER]: '/dashboard/listener',
  [ROLES.ARTIST]: '/dashboard/artist',
  [ROLES.CONTENT_CREATOR]: '/dashboard/content-creator',
  [ROLES.PODCASTER]: '/dashboard/podcaster',
  [ROLES.STREAMER]: '/dashboard/streamer',
  [ROLES.STUDIO]: '/dashboard/studio',
  [ROLES.VIDEOGRAPHER]: '/dashboard/videographer',
  [ROLES.ENGINEER]: '/dashboard/engineer',
  [ROLES.GRAPHIC_DESIGNER]: '/dashboard/graphic-designer',
  [ROLES.PRODUCER]: '/dashboard/producer',
  [ROLES.ORGANIZATION]: '/dashboard/organization',
  [ROLES.ADVERTISER]: '/advertiser/dashboard',
  [ROLES.OWNER]: '/dashboard/artist',
  [ROLES.ADMIN]: '/dashboard/artist'
};

// Role to onboarding path mapping
const ONBOARDING_PATHS = {
  [ROLES.LISTENER]: '/listener/onboarding',
  [ROLES.ARTIST]: '/artist/onboarding',
  [ROLES.CONTENT_CREATOR]: '/content-creator/onboarding',
  [ROLES.PODCASTER]: '/podcaster/onboarding',
  [ROLES.STREAMER]: '/streamer/onboarding',
  [ROLES.STUDIO]: '/studio/onboarding',
  [ROLES.VIDEOGRAPHER]: '/videographer/onboarding',
  [ROLES.ENGINEER]: '/engineer/onboarding',
  [ROLES.GRAPHIC_DESIGNER]: '/graphic-designer/onboarding',
  [ROLES.PRODUCER]: '/producer/onboarding',
  [ROLES.ORGANIZATION]: '/organization/onboarding'
};

/**
 * Get the dashboard path for a given role
 * @param {string} role - User role
 * @returns {string} Dashboard path
 */
export function getDashboardPath(role) {
  const normalizedRole = role?.toLowerCase();
  return DASHBOARD_PATHS[normalizedRole] || '/dashboard/listener';
}

/**
 * Get the onboarding path for a given role
 * @param {string} role - User role
 * @returns {string} Onboarding path
 */
export function getOnboardingPath(role) {
  const normalizedRole = role?.toLowerCase();
  return ONBOARDING_PATHS[normalizedRole] || '/listener/onboarding';
}

/**
 * Determine where to redirect a user after login
 * @param {Object} user - User object from API
 * @returns {string} Redirect path
 */
export function getPostLoginRedirect(user) {
  if (!user) return '/auth';
  
  const role = user.role?.toLowerCase() || 'listener';
  
  // Check if user needs to claim a handle first
  if (!user.handle && !user.has_handle) {
    return '/claim-handle';
  }
  
  // Check if onboarding is complete
  const onboardingComplete = user.onboarding_complete ?? user.onboardingComplete ?? true;
  
  if (!onboardingComplete) {
    return getOnboardingPath(role);
  }
  
  // Default home: Soundstage Spotlight
  return '/soundstage-spotlight';
}

/**
 * Check if a role is a creator role (requires subscription)
 * @param {string} role - User role
 * @returns {boolean}
 */
export function isCreatorRole(role) {
  return CREATOR_ROLES.includes(role?.toLowerCase());
}

/**
 * Check if a role has admin access
 * @param {string} role - User role
 * @returns {boolean}
 */
export function isAdminRole(role) {
  return [ROLES.OWNER, ROLES.ADMIN].includes(role?.toLowerCase());
}

export default {
  ROLES,
  CREATOR_ROLES,
  getDashboardPath,
  getOnboardingPath,
  getPostLoginRedirect,
  isCreatorRole,
  isAdminRole
};
